const AdminSeo = lazy(() => import("@/pages/AdminSeo"));
import {SeoNavigation} from "@/components/SeoNavigation";
import DriverSignup from "@/pages/DriverSignup";
import { useEffect, lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { AuthProvider } from "@/hooks/use-auth";
import { TownProvider } from "@/hooks/use-town";
import { AppLayout } from "@/components/layout/AppLayout";
import { PhoneRequiredModal } from "@/components/PhoneRequiredModal";
import Home from "@/pages/Home";
import News from "@/pages/News";
import NewsDetail from "@/pages/NewsDetail";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import EventsRoute from "@/pages/EventsRoute";
import Places from "@/pages/Places";
import Deals from "@/pages/Deals";
import DealDetail from "@/pages/DealDetail";
import Businesses from "@/pages/Businesses";
import BusinessDetail from "@/pages/BusinessDetail";
import FreeItems from "@/pages/FreeItems";
import Vacancies from "@/pages/Vacancies";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import Account from "@/pages/Account";
import DriverAgreement from "@/pages/DriverAgreement";
import EditProfile from "@/pages/EditProfile";
import Community from "@/pages/Community";
import SubmitBusiness from "@/pages/SubmitBusiness";
import PostJob from "@/pages/PostJob";
import SubmitEvent from "@/pages/SubmitEvent";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminPending from "@/pages/AdminPending";
import AdminUsers from "@/pages/AdminUsers";
import AdminPush from "@/pages/AdminPush";
import AdminCategories from "@/pages/AdminCategories";
import AdminPricing from "@/pages/AdminPricing";
import AdminTips from "@/pages/AdminTips";
import AdminSponsor from "@/pages/AdminSponsor";
import AdminIntegrations from "@/pages/AdminIntegrations";
import AdminReports from "@/pages/AdminReports";
import AdminSupport from "@/pages/AdminSupport";
import AdminOfficePhones from "@/pages/AdminOfficePhones";
import AdminPrinterHealth from "@/pages/AdminPrinterHealth";
import AdminRemoteSupport from "@/pages/AdminRemoteSupport";
import AdminFreeItems from "@/pages/AdminFreeItems";
import AdminNewsCreate from "@/pages/AdminNewsCreate";
import AdminNewsManage from "@/pages/AdminNewsManage";
import AdminTowns from "@/pages/AdminTowns";
import AdminBusinesses from "@/pages/AdminBusinesses";
import AdminReviews from "@/pages/AdminReviews";
import AdminTradespeople from "@/pages/AdminTradespeople";
import AdminCreateEvent from "@/pages/AdminCreateEvent";
import AdminCreateDeal from "@/pages/AdminCreateDeal";
import AdminEvents from "@/pages/AdminEvents";
import AdminDeals from "@/pages/AdminDeals";
import AdminDealRedemptions from "@/pages/AdminDealRedemptions";
import AdminTransactions from "@/pages/AdminTransactions";
import AdminCompetitions from "@/pages/AdminCompetitions";
import Competition from "@/pages/Competition";
import FreeWebsiteApply from "@/pages/FreeWebsiteApply";
import AdminWebsiteBriefs from "@/pages/AdminWebsiteBriefs";
import AdminClaude from "@/pages/AdminClaude";
import TransactionHistory from "@/pages/TransactionHistory";
import BusinessPortal from "@/pages/BusinessPortal";
import OrderStores, { ShopLocalStores } from "@/pages/OrderStores";
import OrderStoreDetail from "@/pages/OrderStoreDetail";
import OrderSuccess from "@/pages/OrderSuccess";
import BusinessOrderStore from "@/pages/BusinessOrderStore";
import FinanceCentre from "@/pages/FinanceCentre";
import AccountPaymentReturn from '@/pages/AccountPaymentReturn';
import AcceptStoreInvite from "@/pages/AcceptStoreInvite";
import AdminOrderStores from "@/pages/AdminOrderStores";
import AdminDrivers from "@/pages/AdminDrivers";
import AdminHubDash from "@/pages/AdminHubDash";
import AdminMultiOrders from "@/pages/AdminMultiOrders";
import AdminLiveOrders from "@/pages/AdminLiveOrders";
import AdminOrders from "@/pages/AdminOrders";
import AdminPaymentLog from "@/pages/AdminPaymentLog";
import AdminCashbook from "@/pages/AdminCashbook";
import AdminGlobalCodes from "@/pages/AdminGlobalCodes";
import Influencer from "@/pages/Influencer";
import AdminInfluencers from "@/pages/AdminInfluencers";
import ReferralLanding from "@/pages/ReferralLanding";
import AdminFeedAds from "@/pages/AdminFeedAds";
import AdminFacebook from "@/pages/AdminFacebook";
import AdminKindness from "@/pages/AdminKindness";
import Advertise from "@/pages/Advertise";
import CreateAd from "@/pages/CreateAd";
import MyAdverts from "@/pages/MyAdverts";
import AdminBankReconciliation from "@/pages/AdminBankReconciliation";
import AdminGuestActivity from "@/pages/AdminGuestActivity";
import AdminReceiptDesign from "@/pages/AdminReceiptDesign";
import AdminCashFees from "@/pages/AdminCashFees";
import AdminDriverWallets from "@/pages/AdminDriverWallets";
import AdminDriverPayouts from "@/pages/AdminDriverPayouts";
import AdminGroceryScanner from "@/pages/AdminGroceryScanner";
import StoreScanner from "@/pages/StoreScanner";
import AdminBoosts from "@/pages/AdminBoosts";
import AdminCashReturns from "@/pages/AdminCashReturns";
import AdminDeliveryRuns from "@/pages/AdminDeliveryRuns";
import AdminDispatchZones from "@/pages/AdminDispatchZones";
import AdminSmsManagement from "@/pages/AdminSmsManagement";
import AdminDeliveryDisputes from "@/pages/AdminDeliveryDisputes";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminHubRevenue from "@/pages/AdminHubRevenue";
import AdminAgencyCompliance from "@/pages/AdminAgencyCompliance";
import AdminReconciliation from "@/pages/AdminReconciliation";
import AdminJobs from "@/pages/AdminJobs";
import AdminVenues from "@/pages/AdminVenues";
import AdminVenueReview from "@/pages/AdminVenueReview";
import AdminDefinitions from "@/pages/AdminDefinitions";
import AdminWallboard from "@/pages/AdminWallboard";
import SavedItems from "@/pages/SavedItems";
import SearchPage from "@/pages/Search";
import OrderPad from "@/pages/OrderPad";
import SalesPortal from "@/pages/SalesPortal";
import AdminSales from "@/pages/AdminSales";
import AdminSalesTracking from "@/pages/AdminSalesTracking";
import MyOrders from "@/pages/MyOrders";
import MyDeals from "@/pages/MyDeals";
import OrderTrack from "@/pages/OrderTrack";
import Messages from "@/pages/Messages";
import CommunityProfile from "@/pages/CommunityProfile";
import Help from "@/pages/Help";
import Support from "@/pages/Support";
import MyJobs from "@/pages/MyJobs";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import BecomeTradesperson from "@/pages/BecomeTradesperson";
import BecomeDriver from "@/pages/BecomeDriver";
import Notifications from "@/pages/Notifications";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CookiePolicy from "@/pages/CookiePolicy";
import { CookieBanner } from "@/components/CookieBanner";
import { PendingOrdersProvider } from "@/context/PendingOrdersContext";
import HubDash from "@/pages/HubDash";
import WhatsOn from "@/pages/WhatsOn";
import Venues from "@/pages/Venues";
import VenueDetail from "@/pages/VenueDetail";
import ListYourVenue from "@/pages/ListYourVenue";
import VenueManagement from "@/pages/VenueManagement";
import AdminCampaigns from "@/pages/AdminCampaigns";
import AdminQuizzes from "@/pages/AdminQuizzes";
import AdminQuizBuilder from "@/pages/AdminQuizBuilder";
import AdminQuizHost from "@/pages/AdminQuizHost";
import AdminQuizProjector from "@/pages/AdminQuizProjector";
import AdminQuizHistory from "@/pages/AdminQuizHistory";
import MultiOrderCompanionPicker from "@/pages/MultiOrderCompanionPicker";
import MultiOrderCheckout from "@/pages/MultiOrderCheckout";
import MultiOrderTracking from "@/pages/MultiOrderTracking";

const queryClient = new QueryClient();

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location]);
  return null;
}

function Router() {
  return (
    <AppLayout>
      <SeoNavigation />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/news" component={News} />
        <Route path="/news/:id" component={NewsDetail} />
        <Route path="/jobs" component={Jobs} />
        <Route path="/jobs/:id" component={JobDetail} />
        <Route path="/post-job" component={PostJob} />
        {/* ── What's On (date-led) ────────────────────────────────────────
            `/events` is the canonical, SEO-indexed surface (sitemap, daily,
            priority 0.8). `/whats-on` stays a live alias rather than a
            redirect, because it is the deep-link target of the "new activity
            at a venue you follow" push payload (`{screen:"/whats-on"}`,
            venueActivities.ts) already delivered to phones in the field.

            `/events/:segment` is ambiguous BY DESIGN — one segment has to
            serve date presets, town listings and event detail. EventsRoute
            resolves it; see the note in that file for why moving the event
            URLs was not an option (audit R10, universal links). */}
        <Route path="/whats-on" component={WhatsOn} />
        <Route path="/events" component={Events} />
        <Route path="/events/:town/:category" component={WhatsOn} />
        <Route path="/events/:segment" component={EventsRoute} />
        {/* Short canonical form for one event. `/events/:id` still resolves
            through EventsRoute, so nothing already indexed moves. */}
        <Route path="/event/:id" component={EventDetail} />
        <Route path="/submit-event" component={SubmitEvent} />
        <Route path="/deals" component={Deals} />
        <Route path="/deals/:id" component={DealDetail} />
        <Route path="/businesses" component={Businesses} />
        <Route path="/businesses/:id" component={BusinessDetail} />
        <Route path="/submit-business" component={SubmitBusiness} />
        {/* ── Places (venue-led) ──────────────────────────────────────────
            `/places` is the new home. `/venues` and `/venues/:id` are kept
            alive — never redirected — because installed mobile binaries and
            already-sent push payloads still resolve them (audit R10). */}
        <Route path="/places" component={Places} />
        <Route path="/places/:town/:facility" component={Places} />
        <Route path="/places/:town" component={Places} />
        <Route path="/venue/:slug" component={VenueDetail} />
        <Route path="/venues" component={Venues} />
        <Route path="/venues/by-slug/:slug" component={VenueDetail} />
        <Route path="/venues/:id" component={VenueDetail} />
        <Route path="/list-your-venue" component={ListYourVenue} />
        <Route path="/my-venues" component={VenueManagement} />
        <Route path="/free-items" component={FreeItems} />
        <Route path="/marketplace" component={FreeItems} />
        <Route path="/vacancies" component={Vacancies} />
        <Route path="/community" component={Community} />
        <Route path="/profile/:id" component={CommunityProfile} />
        {/* Two different things, deliberately two routes.
            /support  = contact the Ayrshire Hub team (works logged out).
            /help     = crisis helplines, food banks, welfare — a directory,
                        which cannot take a message and must stay findable.
            /contact and /unsubscribe are aliases people and legal docs guess
            at; both land on the form, the latter pre-filtered to the opt-out. */}
        <Route path="/support">{() => <Support />}</Route>
        <Route path="/contact">{() => <Support />}</Route>
        <Route path="/unsubscribe">{() => <Support defaultCategory="unsubscribe" />}</Route>
        <Route path="/help" component={Help} />
        <Route path="/hub-dash" component={HubDash} />
        <Route path="/my-jobs" component={MyJobs} />
        <Route path="/messages" component={Messages} />
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/login" component={Login} />
        <Route path="/signup" component={Signup} />
        <Route path="/account" component={Account} />
        <Route path="/account/influencer" component={Influencer} />
        <Route path="/r/:code" component={ReferralLanding} />
        <Route path="/edit-profile" component={EditProfile} />
        <Route path="/admin/seo"><Suspense fallback={<p>Loading search visibility…</p>}><AdminSeo /></Suspense></Route>
        <Route path="/admin" component={AdminDashboard} />
        <Route path="/admin/pending" component={AdminPending} />
        <Route path="/admin/users" component={AdminUsers} />
        <Route path="/admin/categories" component={AdminCategories} />
        <Route path="/admin/sponsor" component={AdminSponsor} />
        <Route path="/admin/pricing" component={AdminPricing} />
        <Route path="/admin/tips" component={AdminTips} />
        <Route path="/admin/push" component={AdminPush} />
        <Route path="/admin/messages" component={AdminPush} />
        <Route path="/admin/campaigns" component={AdminCampaigns} />
        <Route path="/admin/integrations" component={AdminIntegrations} />
        <Route path="/admin/reports" component={AdminReports} />
        <Route path="/admin/support" component={AdminSupport} />
        <Route path="/admin/office-phones" component={AdminOfficePhones} />
        <Route path="/admin/printer-health" component={AdminPrinterHealth} />
        <Route path="/admin/remote-support" component={AdminRemoteSupport} />
        <Route path="/admin/free-items" component={AdminFreeItems} />
        <Route path="/admin/news/create" component={AdminNewsCreate} />
        <Route path="/admin/news" component={AdminNewsManage} />
        <Route path="/admin/towns" component={AdminTowns} />
        <Route path="/admin/businesses" component={AdminBusinesses} />
        <Route path="/admin/venue-review" component={AdminVenueReview} />
        <Route path="/admin/definitions" component={AdminDefinitions} />
        <Route path="/admin/venues" component={AdminVenues} />
        <Route path="/admin/reviews" component={AdminReviews} />
        <Route path="/admin/trades" component={AdminTradespeople} />
        <Route path="/admin/events/create" component={AdminCreateEvent} />
        <Route path="/admin/deals/create" component={AdminCreateDeal} />
        <Route path="/admin/events" component={AdminEvents} />
        <Route path="/admin/deals" component={AdminDeals} />
        <Route path="/admin/deal-redemptions" component={AdminDealRedemptions} />
        <Route path="/admin/wallboard" component={AdminWallboard} />
        <Route path="/admin/hub-dash" component={AdminHubDash} />
        <Route path="/admin/live-orders" component={AdminLiveOrders} />
        <Route path="/admin/multi-orders" component={AdminMultiOrders} />
        <Route path="/admin/payment-log" component={AdminPaymentLog} />
        <Route path="/admin/orders" component={AdminOrders} />
        <Route path="/admin/guest-activity" component={AdminGuestActivity} />
        <Route path="/admin/order-stores" component={AdminOrderStores} />
        <Route path="/admin/drivers" component={AdminDrivers} />
        <Route path="/admin/receipt-design" component={AdminReceiptDesign} />
        <Route path="/admin/cash-fees" component={AdminCashFees} />
        <Route path="/admin/cashbook" component={AdminCashbook} />
        <Route path="/admin/global-codes" component={AdminGlobalCodes} />
        <Route path="/admin/influencers" component={AdminInfluencers} />
        <Route path="/admin/feed-ads" component={AdminFeedAds} />
        <Route path="/admin/facebook" component={AdminFacebook} />
        <Route path="/admin/kindness" component={AdminKindness} />
        <Route path="/advertise" component={Advertise} />
        <Route path="/create-ad" component={CreateAd} />
        <Route path="/my-adverts" component={MyAdverts} />
        <Route path="/admin/bank-reconciliation" component={AdminBankReconciliation} />
        <Route path="/admin/driver-wallets" component={AdminDriverWallets} />
        <Route path="/admin/driver-payouts" component={AdminDriverPayouts} />
        <Route path="/admin/grocery-scan">{() => { window.location.replace('/store/scan'); return null; }}</Route>
        <Route path="/store/scan" component={StoreScanner} />
        <Route path="/admin/boosts" component={AdminBoosts} />
        <Route path="/admin/cash-returns" component={AdminCashReturns} />
        <Route path="/admin/delivery-runs" component={AdminDeliveryRuns} />
        <Route path="/admin/dispatch-zones" component={AdminDispatchZones} />
        <Route path="/admin/delivery-disputes" component={AdminDeliveryDisputes} />
        <Route path="/admin/audit-log" component={AdminAuditLog} />
        <Route path="/admin/sms" component={AdminSmsManagement} />
        <Route path="/admin/hub-revenue" component={AdminHubRevenue} />
        <Route path="/admin/agency-compliance" component={AdminAgencyCompliance} />
        <Route path="/admin/analytics" component={AdminAnalytics} />
        <Route path="/admin/reconciliation" component={AdminReconciliation} />
        <Route path="/my-orders" component={MyOrders} />
        <Route path="/my-deals" component={MyDeals} />
        <Route path="/order/track/:orderId" component={OrderTrack} />
        <Route path="/order/success" component={OrderSuccess} />
        <Route path="/order/accept-invite" component={AcceptStoreInvite} />
        <Route path="/order/multi-pick" component={MultiOrderCompanionPicker} />
        <Route path="/order/multi-checkout" component={MultiOrderCheckout} />
        <Route path="/order/multi-track/:multiOrderId" component={MultiOrderTracking} />
        <Route path="/order/:slug">{() => <OrderStoreDetail />}</Route>
        <Route path="/order" component={OrderStores} />
        <Route path="/shop-local" component={ShopLocalStores} />
        <Route path="/business/order-store/:id" component={BusinessOrderStore} />
        <Route path="/business/order-store" component={BusinessOrderStore} />
        <Route path="/business/finance/:id" component={FinanceCentre} />
        <Route path="/account-payment-return" component={AccountPaymentReturn} />
        <Route path="/business/order-pad" component={OrderPad} />
        <Route path="/sales" component={SalesPortal} />
        <Route path="/sales/:rest*" component={SalesPortal} />
        <Route path="/admin/sales" component={AdminSalesTracking} />
        <Route path="/admin/sales-tracking" component={AdminSalesTracking} />
        <Route path="/admin/transactions" component={AdminTransactions} />
        <Route path="/my-transactions" component={TransactionHistory} />
        <Route path="/admin/content/jobs" component={AdminJobs} />
        <Route path="/notifications" component={Notifications} />
        <Route path="/saved" component={SavedItems} />
        <Route path="/search" component={SearchPage} />
        <Route path="/become-tradesperson" component={BecomeTradesperson} />
        <Route path="/drivers" component={DriverSignup} />
        <Route path="/become-a-driver" component={BecomeDriver} />
        <Route path="/driver-agreement" component={DriverAgreement} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/privacy" component={PrivacyPolicy} />
        <Route path="/terms" component={TermsOfService} />
        <Route path="/cookie-policy" component={CookiePolicy} />
        <Route path="/competition" component={Competition} />
        <Route path="/free-website" component={FreeWebsiteApply} />
        <Route path="/admin/website-briefs" component={AdminWebsiteBriefs} />
        <Route path="/admin/competitions" component={AdminCompetitions} />
        <Route path="/admin/claude" component={AdminClaude} />
        <Route path="/admin/quiz/history" component={AdminQuizHistory} />
        <Route path="/admin/quiz/projector/:sessionId" component={AdminQuizProjector} />
        <Route path="/admin/quiz/host/:sessionId" component={AdminQuizHost} />
        <Route path="/admin/quiz/new" component={AdminQuizBuilder} />
        <Route path="/admin/quiz/:id/edit" component={AdminQuizBuilder} />
        <Route path="/admin/quiz" component={AdminQuizzes} />
        <Route path="/business" component={BusinessPortal} />
        <Route path="/business/:rest*" component={BusinessPortal} />
        <Route component={NotFound} />
      </Switch>
    </AppLayout>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <PendingOrdersProvider>
          <TownProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
              <ScrollToTop />
              <Router />
              {/* OnboardingWizard removed — no longer prompt new users with "do you own a
                  business / are you a trade?" on first visit. Business listing + tradesperson
                  signup remain available from the menus/pages. */}
              <PhoneRequiredModal />
              <CookieBanner />
            </WouterRouter>
            <Toaster />
          </TownProvider>
          </PendingOrdersProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
